import { useEffect, useRef, useState } from 'react';

export default function LeadModal({ open, onClose, shortId, defaultOrg }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [org, setOrg] = useState(defaultOrg || '');
  const [phone, setPhone] = useState('');
  const [data, setData] = useState('');
  const [deadline, setDeadline] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const firstRef = useRef(null);

  useEffect(() => { if (open) { firstRef.current?.focus(); setDone(false); setError(''); } }, [open]);
  useEffect(() => { setOrg(defaultOrg || ''); }, [defaultOrg]);

  if (!open) return null;

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const r = await fetch('/api/lead', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ name, email, org, phone, data, deadline, shortId }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.error || `Submit failed (${r.status})`);
      }
      setDone(true);
    } catch (e) {
      setError(e.message || 'Submit failed. Email david@atlasgeo.ai directly.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="lead-title"
      onClick={onClose}
    >
      <div
        className="card w-full max-w-md p-6 bg-white"
        onClick={(e) => e.stopPropagation()}
      >
        {done ? (
          <div className="text-center py-4">
            <h2 id="lead-title" className="text-lg font-semibold text-atlas-ink">Thanks — we&rsquo;ll be in touch</h2>
            <p className="mt-2 text-sm text-atlas-muted">
              David from AtlasGeo will follow up within one business day.
            </p>
            <button type="button" onClick={onClose} className="btn-primary mt-5">Close</button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <div className="flex items-start justify-between">
              <h2 id="lead-title" className="text-lg font-semibold text-atlas-ink">Request full analysis</h2>
              <button type="button" aria-label="Close" onClick={onClose} className="text-atlas-muted text-lg leading-none">×</button>
            </div>
            <p className="text-sm text-atlas-muted">
              Tell us a little about your project. We&rsquo;ll send back a real analysis using your data.
            </p>
            <ModalField ref={firstRef} id="lm-name" label="Name" value={name} onChange={setName} required />
            <ModalField id="lm-email" label="Email" type="email" value={email} onChange={setEmail} required />
            <ModalField id="lm-org" label="Organization" value={org} onChange={setOrg} required />
            <ModalField id="lm-phone" label="Phone (optional)" value={phone} onChange={setPhone} />
            <div>
              <label htmlFor="lm-data" className="block text-sm font-medium text-atlas-ink mb-1">What data do you have?</label>
              <textarea
                id="lm-data"
                value={data}
                onChange={(e) => setData(e.target.value)}
                rows={2}
                maxLength={500}
                className="w-full border border-atlas-line rounded-md px-3 py-2 text-sm"
                placeholder="e.g. 3 years of crash CSVs from VDOT"
              />
            </div>
            <ModalField id="lm-deadline" label="Deadline (optional)" value={deadline} onChange={setDeadline} placeholder="e.g. SS4A app due Aug 1" />
            {error && <p className="text-xs text-red-600" role="alert">{error}</p>}
            <div className="flex gap-2 pt-1">
              <button type="submit" disabled={busy} className="btn-primary flex-1">
                {busy ? 'Sending…' : 'Send request'}
              </button>
              <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

import { forwardRef } from 'react';
const ModalField = forwardRef(function ModalField({ id, label, value, onChange, required, type = 'text', placeholder }, ref) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-atlas-ink mb-1">
        {label}{required && <span className="text-atlas-gold-dark"> *</span>}
      </label>
      <input
        ref={ref}
        id={id}
        type={type}
        value={value}
        required={required}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-atlas-line rounded-md px-3 py-2 text-sm"
      />
    </div>
  );
});
